import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Table, Button, Tag, Space } from "antd";
import { getCampaignSentBatches } from "../../../api/Services";
import styles from "../Campaigns.module.scss";
import moment from "moment";

const SentCampaigns: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const getBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/campaigns";
    }
    return "/promotions/campaigns";
  };

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(1);

  const fetchBatches = async (page = actualPage) => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await getCampaignSentBatches(id, page);
      setData(response?.batches || []);
      setTotalSize(response?.totalElements || 0);
    } catch (e) {
      setData([]);
      setTotalSize(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, actualPage]);

  const handlePagination = (page: number) => {
    setActualPage(page);
    fetchBatches(page);
  };

  const columns = [
    {
      title: t("Common-Sent", "Enviada"),
      dataIndex: "sentAt",
      render: (date: any) =>
        date ? moment(date).format("DD-MM-YYYY HH:mm") : "-",
    },
    {
      title: t("Common-Template", "Plantilla"),
      dataIndex: "templateName",
      render: (name: string) => name || "-",
    },
    {
      title: t("Common-Recipients", "Destinatarios"),
      dataIndex: "recipientsCount",
      render: (value: number) => (value != null ? value : "-"),
    },
    {
      title: t("Common-Status", "Estado"),
      key: "status",
      render: (_: any, record: any) => {
        const total = Number(record.recipientsCount || 0);
        const delivered = Number(record.deliveredCount || 0);
        const read = Number(record.readCount || 0);
        const failed = Number(record.failedCount || 0);
        const pending = Math.max(0, total - delivered - read - failed);
        return (
          <Space>
            <Tag color="purple">
              {t("Common-MessageStatusRead", "Leído")}s: {read}
            </Tag>
            <Tag color="green">
              {t("Common-MessageStatusDelivered", "Entregado")}s: {delivered}
            </Tag>
            <Tag color="orange">Pendientes: {pending}</Tag>
            <Tag color="red">
              {t("Common-MessageStatusFailed", "Fallido")}s: {failed}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: t("Common-Actions", "Acciones"),
      key: "actions",
      render: (_: any, record: any) => (
        <Button
          onClick={() => {
            if (!id) return;
            navigate(`${getBasePath()}/${id}/sent/${record.sendBatchId}`);
          }}
        >
          {t("Common-View", "Ver")}
        </Button>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("Common-CampaignSentsTitle")}</h1>
        <div className={styles.actionButtonsWrapper}>
          <Button
            onClick={() => navigate(getBasePath())}
            className={styles.newButton}
          >
            {t("Common-Back")}
          </Button>
        </div>
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.tableWrapper}>
          <Table
            dataSource={data}
            columns={columns}
            rowKey="sendBatchId"
            loading={loading}
            pagination={{
              current: actualPage,
              pageSize: 10,
              total: totalSize,
              onChange: handlePagination,
            }}
            size="small"
          />
        </div>
      </div>
    </div>
  );
};

export default SentCampaigns;


